<template>
  <div class="px-4 pb-4">
    <div class="d-flex align-items-center mb-2">
      <h4 class="mb-0">Historial · últimas {{ retentionWeeks }} semanas</h4>
      <base-button
        type="secondary"
        size="sm"
        class="ml-3"
        :loading="isLoading"
        @click="load"
      >Actualizar</base-button>
    </div>

    <p class="text-xs text-muted mb-2">
      El forecast se guarda <b>todos los días de madrugada</b>, entre o no entre alguien
      acá, y se sobreescribe si después se descarga el reporte de ese día. Quedan
      {{ retentionWeeks }} semanas con ventana <b>rodante</b>: cuando entra una semana
      nueva se cae la más vieja, sola. La semana va de <b>sábado a viernes</b> y lleva el
      mismo número que usa el resto del sistema.
      Siempre con el canal <b>Todos</b>, que es con el que se cocina: no cambia con el
      selector de canal de arriba.
      Los días marcados <b>reconstruido</b> se calcularon después para completar la serie:
      salen del modelo de hoy aplicado a esa fecha, así que todos los martes del mes dan lo
      mismo. No son lo que el sistema pidió ese día.
    </p>

    <div v-if="errorMessage" class="text-xs notice severe mb-2">{{ errorMessage }}</div>

    <div v-else-if="!isEnabled" class="text-xs notice mb-2">
      <b>El historial todavía no está activo.</b>
      Falta correr la migración de <code>forecast_historial_diario</code> en este entorno.
      Los reportes se generan igual; solo no quedan guardados.
    </div>

    <div v-else-if="isLoading && !weeks.length" class="text-muted text-sm py-3">
      Cargando historial…
    </div>

    <div v-else-if="!weeks.length" class="text-muted text-sm py-3">
      Todavía no hay nada guardado.
    </div>

    <forecast-history-week
      v-for="week in weeks"
      :key="week.startDate"
      :week="week"
      :is-open="openWeekStartDate === week.startDate"
      :details="details"
      :details-row-id="detailsRowId"
      :details-title="detailsTitle"
      :downloading-id="downloadingRowId"
      :opening-id="openingRowId"
      @toggle="toggleWeek"
      @download="downloadPdf"
      @show-details="showDetails"
    ></forecast-history-week>
  </div>
</template>
<script>
import ForecastGateway from "@/gateways/ForecastGateway";
import ForecastHistoryWeek from "@/components/ForecastHistoryWeek";

const PREQUANTITIES_REPORT = "precantidades";
const DEFAULT_RETENTION_WEEKS = 4;

export default {
  name: "forecast-history",
  components: { ForecastHistoryWeek },
  props: {
    kitchenId: { type: Number, default: 0 },
  },
  data() {
    return {
      isLoading: false,
      isEnabled: true,
      errorMessage: null,
      retentionWeeks: DEFAULT_RETENTION_WEEKS,
      rows: [],
      openWeekStartDate: null,
      details: [],
      detailsRowId: null,
      detailsTitle: "",
      openingRowId: null,
      downloadingRowId: null,
    };
  },
  computed: {
    weeks() {
      const today = this.todayIso();
      const byStartDate = {};

      this.rows.forEach((row) => {
        const startDate = row.semana_inicio;
        if (!byStartDate[startDate]) {
          byStartDate[startDate] = {
            startDate,
            endDate: row.semana_fin,
            number: row.semana_num,
            year: row.semana_anio,
            isCurrent: row.semana_inicio <= today && today <= row.semana_fin,
            days: [],
            kg: 0,
          };
        }
        byStartDate[startDate].days.push(row);
        byStartDate[startDate].kg += row.kg;
      });

      return Object.values(byStartDate).sort((first, second) =>
        first.startDate < second.startDate ? 1 : -1
      );
    },
  },
  watch: {
    kitchenId() {
      this.load();
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    todayIso() {
      const today = new Date();
      return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(
        2,
        "0"
      )}-${String(today.getDate()).padStart(2, "0")}`;
    },
    resetDetails() {
      this.details = [];
      this.detailsRowId = null;
    },
    toggleWeek(startDate) {
      this.openWeekStartDate =
        this.openWeekStartDate === startDate ? null : startDate;
      this.resetDetails();
    },
    apiMessage(error) {
      const payload =
        error && error.response && error.response.data && error.response.data.payload;
      return (payload && payload.message) || (error && error.message) || "";
    },
    async load() {
      this.isLoading = true;
      this.errorMessage = null;
      this.resetDetails();
      try {
        const response = await ForecastGateway.history(
          PREQUANTITIES_REPORT,
          this.kitchenId
        );
        const result =
          (response.data && response.data.payload && response.data.payload.result) ||
          response.data;
        this.rows = ((result && result.historial) || []).map((row) => ({
          ...row,
          kg: Number(row.kg),
        }));
        this.isEnabled = !(result && result.disponible === false);
        if (result && result.retencion_semanas) {
          this.retentionWeeks = result.retencion_semanas;
        }
        this.openWeekStartDate = this.weeks.length ? this.weeks[0].startDate : null;
      } catch (error) {
        this.errorMessage = "No se pudo leer el historial. " + this.apiMessage(error);
      } finally {
        this.isLoading = false;
      }
    },
    async showDetails(row) {
      if (this.detailsRowId === row.id) {
        this.resetDetails();
        return;
      }
      this.openingRowId = row.id;
      try {
        const response = await ForecastGateway.history(
          PREQUANTITIES_REPORT,
          this.kitchenId,
          "",
          row.id
        );
        const result =
          (response.data && response.data.payload && response.data.payload.result) ||
          response.data;
        const savedRow = ((result && result.historial) || [])[0];
        this.details = (savedRow && savedRow.detalle) || [];
        this.detailsRowId = row.id;
        this.detailsTitle = `${row.cocina} · ${row.fecha}`;
      } catch (error) {
        this.errorMessage = "No se pudo abrir el detalle. " + this.apiMessage(error);
      } finally {
        this.openingRowId = null;
      }
    },
    async downloadPdf(row) {
      this.downloadingRowId = row.id;
      try {
        const response = await ForecastGateway.historyPdf(row.id);
        const disposition =
          response.headers && response.headers["content-disposition"];
        const fromHeader = disposition && disposition.match(/filename="?([^";]+)"?/i);
        const url = URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.download = fromHeader
          ? fromHeader[1]
          : `precantidades-historial-${row.fecha}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch (error) {
        alert(
          "No se pudo bajar el PDF de ese día. " +
            (await this.blobMessage(error))
        );
      } finally {
        this.downloadingRowId = null;
      }
    },
    async blobMessage(error) {
      const data = error && error.response && error.response.data;
      if (!(data instanceof Blob)) {
        return this.apiMessage(error);
      }
      try {
        const parsed = JSON.parse(await data.text());
        return (parsed.payload && parsed.payload.message) || "";
      } catch (parseError) {
        return this.apiMessage(error);
      }
    },
  },
};
</script>
<style scoped>
.notice {
  background: #fff6e0;
  border-left: 3px solid #d9a400;
  color: #6b5200;
  padding: 8px 10px;
  border-radius: 3px;
}
.notice.severe {
  background: #fdeceb;
  border-left-color: #c0392b;
  color: #7d241a;
}
</style>
